import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '@/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const colors = [
  { id: 'obsidian', name: 'Obsidian Black', hex: '#000000', desc: 'Absolute light suppression. Engineered for high-intensity glare environments and pure anonymity.' },
  { id: 'amber', name: 'Amber Core', hex: '#f59e0b', desc: 'Contrast-enhancing filter that sharpens detail in low-light and overcast conditions. A modern take on vintage clarity.' },
  { id: 'rose', name: 'Rose Prism', hex: '#db2777', desc: 'Optimized for high-altitude visual clarity. Reduces blue light and eye fatigue during prolonged outdoor use.' },
  { id: 'ocean', name: 'Ocean Blue', hex: '#0284c7', desc: 'Specialized for marine and coastal environments. Neutralizes harsh reflections from water surfaces.' }
];

const LensExplorer = () => {
  const [activeColor, setActiveColor] = useState(colors[0]);

  return (
    <section id="lenses" className="py-32 px-6 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-32 items-center">
        
        {/* Interactive Visualizer */}
        <div className="relative aspect-square md:aspect-video lg:aspect-square bg-gray-50 flex items-center justify-center border-4 border-black overflow-hidden group">
          <div className="absolute inset-0 grid-bg opacity-10"></div>
          
          <div className="relative z-10 w-full px-8 md:px-16">
            <svg viewBox="0 0 400 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto drop-shadow-[0_35px_35px_rgba(0,0,0,0.15)]">
              {/* Lenses with dynamic color */}
              <motion.path 
                animate={{ fill: activeColor.hex }}
                transition={{ duration: 0.6, ease: "circOut" }}
                d="M 50 20 C 130 20, 160 50, 160 90 C 160 130, 100 140, 50 140 C 10 140, 10 80, 50 20 Z" 
                opacity="0.75"
                style={{ mixBlendMode: 'multiply' }}
              />
              <motion.path 
                animate={{ fill: activeColor.hex }}
                transition={{ duration: 0.6, ease: "circOut" }}
                d="M 350 20 C 270 20, 240 50, 240 90 C 240 130, 300 140, 350 140 C 390 140, 390 80, 350 20 Z" 
                opacity="0.75"
                style={{ mixBlendMode: 'multiply' }}
              />
              
              {/* Frame Details */}
              <path d="M 45 15 C 135 15, 170 45, 170 90 C 170 135, 105 145, 45 145 C 0 145, 0 80, 45 15 Z" stroke="black" strokeWidth="14" strokeLinejoin="round" />
              <path d="M 355 15 C 265 15, 230 45, 230 90 C 230 135, 295 145, 355 145 C 400 145, 400 80, 355 15 Z" stroke="black" strokeWidth="14" strokeLinejoin="round" />
              <path d="M 170 50 Q 200 30 230 50" stroke="black" strokeWidth="14" fill="none" strokeLinecap="round" />
              <path d="M 10 40 L -30 30" stroke="black" strokeWidth="14" strokeLinecap="round" />
              <path d="M 390 40 L 430 30" stroke="black" strokeWidth="14" strokeLinecap="round" />
            </svg>
          </div>

          <div className="absolute bottom-8 left-8 flex items-center gap-3">
            <div className="w-3 h-3 rounded-full prism-gradient animate-pulse"></div>
            <span className="text-[10px] font-black tracking-[0.4em] uppercase">Studio Simulation</span>
          </div>
        </div>

        {/* Info & Selectors */}
        <div className="flex flex-col h-full justify-center">
          <h2 className="text-6xl md:text-8xl font-black tracking-tighter uppercase mb-8 leading-none">
            Lens<br/><span className="prism-text">Studio.</span>
          </h2>
          <p className="text-xl text-gray-500 font-medium mb-16 max-w-md leading-relaxed">
            Our chromatic lab develops proprietary optics that adapt to your environment without compromising color fidelity.
          </p>
          
          <div className="space-y-12">
            <div className="flex flex-wrap gap-6">
              {colors.map((color) => (
                <button 
                  key={color.id}
                  onClick={() => setActiveColor(color)}
                  className="group flex flex-col items-center gap-3"
                >
                  <div 
                    className={`w-16 h-16 rounded-none border-2 transition-all duration-500 flex items-center justify-center ${
                      activeColor.id === color.id ? 'border-black scale-110' : 'border-transparent hover:border-gray-200'
                    }`}
                  >
                    <div 
                      className="w-12 h-12 shadow-inner" 
                      style={{ backgroundColor: color.hex, opacity: 0.8 }}
                    />
                  </div>
                  <span className={`text-[10px] font-black tracking-widest uppercase transition-colors ${
                    activeColor.id === color.id ? 'text-black' : 'text-gray-400 group-hover:text-gray-600'
                  }`}>
                    {color.id}
                  </span>
                </button>
              ))}
            </div>
            
            <AnimatePresence mode="wait">
              <motion.div 
                key={activeColor.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="border-l-4 border-black pl-8 py-2 max-w-md"
              >
                <h3 className="text-3xl font-black uppercase mb-4 tracking-tight">{activeColor.name}</h3>
                <p className="text-lg text-gray-600 font-medium leading-relaxed">{activeColor.desc}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LensExplorer;